import { CheckIcon } from '@assets/icons/CheckIcon';
import { useWindowSize } from '@hooks/useWindowSize';
import { ArrowBack } from '@mui/icons-material';
import {
	Button,
	Paper,
	Stack,
	Step,
	StepConnector,
	StepLabel,
	Stepper,
	styled,
} from '@mui/material';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import './AuthStepper.css';

// Custom Stepper styles
const QontoConnector = styled(StepConnector)(() => ({
	[`&.${stepConnectorClasses.alternativeLabel}`]: {
		top: 10,
		left: 'calc(-50% + 8px)',
		right: 'calc(50% + 8px)',
	},
	[`&.${stepConnectorClasses.active}`]: {
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: '#fff',
		},
	},
	[`&.${stepConnectorClasses.completed}`]: {
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: '#fff',
		},
	},
	[`& .${stepConnectorClasses.line}`]: {
		borderColor: '#B3DBD6',
		borderTopWidth: 3,
		borderRadius: 1,
	},
}));

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean } }>(() => ({
	color: '#eaeaf0',
	display: 'flex',
	height: 22,
	alignItems: 'center',
	'& .QontoStepIcon-circle': {
		width: 16,
		height: 16,
		borderRadius: '50%',
		backgroundColor: 'currentColor',
	},
}));

function QontoStepIcon(props: StepIconProps) {
	const { active, completed, className } = props;

	return (
		<QontoStepIconRoot ownerState={{ active }} className={className}>
			{active || completed ? (
				// add check icon here when completed
				<div className="QontoStepIcon-circle !bg-[#ffffff] flex items-center justify-center">
					{completed ? <CheckIcon className="w-[14px] h-[14px]" /> : null}
				</div>
			) : (
				<div className="QontoStepIcon-circle !bg-[#B3DBD6]" />
			)}
		</QontoStepIconRoot>
	);
}

interface CustomStepperLayoutProps {
	steps: string[];
	header?: React.ReactNode;
	footer?: React.ReactNode;
	content?: React.ReactNode;
	activeStepIndex?: number;
	hFull?: boolean;
	showBackButton?: boolean;
	onBackButtonClick?: () => void;
}

export const AuthStepper: React.FC<CustomStepperLayoutProps> = ({
	steps,
	header,
	footer,
	content,
	activeStepIndex,
	hFull,
	showBackButton = false,
	onBackButtonClick,
}) => {
	const { t } = useTranslation();
	const size = useWindowSize();

	return (
		<Stack spacing={!hFull ? '36px' : 0} height={hFull && size.width > 576 ? '100%' : 'auto'}>
			{header && <div className="pt-[50px]">{header}</div>}
			<div
				className={classNames('flex items-center justify-center flex-col relative mt-[40px]', {
					'h-full min-h-fit': hFull,
				})}
			>
				<Stepper
					className="bg-[#038877] h-[80px] rounded-lg absolute top-[-40px] pt-[18px] w-[350px] md:w-[662px] lg:w-[762px]"
					alternativeLabel
					activeStep={activeStepIndex}
					connector={<QontoConnector />}
				>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel
								sx={{ '& .MuiStepLabel-labelContainer .MuiStepLabel-label': { marginTop: '6px' } }}
								StepIconComponent={QontoStepIcon}
							>
								<span className="text-white uppercase text-[12px] sm:text-[12px] md:text-[14px] lg:text[16px]">
									{t(label)}
								</span>
							</StepLabel>
						</Step>
					))}
				</Stepper>
				<Paper
					elevation={3}
					className={classNames('!rounded-xl', {
						'h-full w-full': hFull,
					})}
				>
					<div
						className={classNames('relative', {
							'h-full w-full': hFull,
							'w-[350px] sm:w-[450px] md:w-[602px] lg:w-[702px] xl:w-[802px] px-[20px] sm:px-[40px] md:px-[126px] xl:px-[176px] sm:py-[70px] py-[50px] ':
								!hFull,
						})}
					>
						{showBackButton && (
							<Button
								onClick={onBackButtonClick}
								className="!absolute top-[-109px] sm:top-[-120px] left-[-20px] sm:left-[-26px] flex gap-[10px]"
								variant="text"
								color="inherit"
							>
								<ArrowBack /> {t('previous')}
							</Button>
						)}
						{content || <Outlet />}
					</div>
				</Paper>
			</div>
			{footer}
		</Stack>
	);
};
